/// <reference lib="dom" />
//
// Heat-network designer island.
//
// Place a boiler on the map, then branch pipe runs from any node (recursively,
// to any depth) with one click per branch — the run always starts at the
// selected node, so you only click where it ends. Each branch end is an
// "estate" with a power demand (kW) + ΔT. The app sizes every segment against
// the AustroPUR PEX tables, computes flow, pressure drop and heat-loss
// temperature drop, renders a Mermaid schematic, and adds the matched pipes to
// the cart. Bends are made by tapping a run to drop a junction and dragging it.
// Everything is client-side; the network is persisted to localStorage.

import L from "leaflet";
import {
  flowM3h,
  massFlowKgS,
  effectiveDeltaT,
  uWertFor,
  tempDropK,
  chooseSegmentPipe,
  boreThresholdMm,
  type PipeType,
} from "../shared/districtHeating.js";

export {}; // module scope

const MAX_ZOOM = 22;
const STORAGE_KEY = "cflex:heatnet:v1";
const PUMP_WARN_BAR = 6;
/** Static head of a water column: ρgh, in bar per metre of elevation. */
const BAR_PER_M_WATER = 0.0981;

type PipeProduct = {
  variantId: number;
  sku: string;
  name: string;
  type: string;
  jacket: string;
  diameter: number;
  priceCents: number | null;
  currency: string;
  priceFallback: boolean;
  priceText: string;
  imageUrl: string | null;
  href: string;
};
type WallEntryKind = "mauerkragen" | "pressring" | "dichtungskette";
type SealProduct = {
  variantId: number; sku: string; name: string; material: string; size: number;
  priceText: string; priceFallback: boolean; imageUrl: string | null; href: string;
  priceCents?: number | null; currency?: string;
};
type FitOption = {
  kind: WallEntryKind;
  variantId: number;
  sku: string;
  name: string;
  minMm: number;
  maxMm: number;
  kb: number | null;
  priceCents: number | null;
  currency: string;
  priceText: string;
  href: string;
};
type TeePart = { variantId: number; sku: string; name: string; priceCents: number | null; currency: string; priceText: string; href: string };
type DistrictData = {
  products: PipeProduct[];
  wallEntries: FitOption[];
  chains: SealProduct[];
  tee: TeePart | null;
  contact: { phone: string; email: string };
  currency: string;
  lang: string;
  strings: Record<string, string>;
};

type NodeRec = {
  id: string;
  lat: number;
  lng: number;
  kind: "boiler" | "node";
  powerKw: number;
  deltaT: number;
  name: string;
  /** Wall penetration the client wants at this estate. */
  wallEntry?: WallEntryKind;
  /** Ground elevation (m) from the terrain API; undefined = not yet fetched. */
  elevM?: number | null;
};
type SegRec = { id: string; fromId: string; toId: string; latlngs: [number, number][] };
type Persisted = { nodes: NodeRec[]; segments: SegRec[]; view?: { center: [number, number]; zoom: number } };

// Per-segment engineering result.
type SegResult = {
  lengthM: number;
  powerKw: number;
  effDeltaT: number;
  flow: number; // m³/h
  diameter: number;
  type: "Duo" | "Single";
  dropPerM: number;
  dropPa: number; // supply+return
  tempDropK: number;
  product: PipeProduct | null;
  exceedsGradient: boolean;
  noProduct: boolean;
};

const mapEl = document.getElementById("district-map");
const dataEl = document.getElementById("district-data");
const resultsEl = document.getElementById("district-results");
const selectedEl = document.getElementById("dh-selected");
if (mapEl && dataEl && resultsEl && selectedEl) {
  boot(mapEl, dataEl, resultsEl, selectedEl);
}

function boot(mapEl: HTMLElement, dataEl: HTMLElement, resultsEl: HTMLElement, selectedEl: HTMLElement) {
  injectLeafletCss();
  const data = parseData(dataEl);
  const s = data.strings;
  const locale = data.lang === "de" ? "de-DE" : "en-US";
  const nf0 = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
  const nf1 = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  const nf2 = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
  const tr = (k: string, v?: Record<string, string | number>) =>
    (s[k] ?? k).replace(/\{(\w+)\}/g, (_, key) => (v && key in v ? String(v[key]) : `{${key}}`));

  // ---- state ----
  let state: Persisted = loadState();
  let selectedId: string | null = null;
  let results = new Map<string, SegResult>(); // keyed by segment id

  // ---- map + layers ----
  const esri = L.tileLayer(
    "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    { maxZoom: MAX_ZOOM, maxNativeZoom: 19, attribution: "Imagery © Esri" },
  );
  const osm = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    maxZoom: MAX_ZOOM,
    maxNativeZoom: 19,
    attribution: "© OpenStreetMap contributors",
  });
  const labels = L.tileLayer(
    "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
    { maxZoom: MAX_ZOOM, maxNativeZoom: 19, attribution: "Labels © Esri" },
  );
  const view = state.view;
  const map = L.map(mapEl, {
    center: view?.center ?? [47.6, 13.35],
    zoom: view?.zoom ?? 7,
    maxZoom: MAX_ZOOM,
    zoomControl: true,
    layers: [osm],
  });
  const layersCtl = L.control
    .layers({ "Map (OpenStreetMap)": osm, Satellite: esri }, { "Place labels": labels }, { collapsed: true, position: "bottomright" })
    .addTo(map);
  let vectorLayer: L.Layer | null = null;
  loadMapLibre()
    .then(() => {
      vectorLayer = (L as any).maplibreGL({ style: "https://tiles.openfreemap.org/styles/liberty", attribution: "© OpenFreeMap" });
      layersCtl.addBaseLayer(vectorLayer!, "Vector (crisp)");
    })
    .catch(() => {});
  map.on("baselayerchange", (e: any) => {
    if (e.layer === esri) { if (!map.hasLayer(labels)) labels.addTo(map); }
    else if (map.hasLayer(labels)) map.removeLayer(labels);
  });
  map.on("moveend", saveSoon);
  setTimeout(() => map.invalidateSize(), 0);
  window.addEventListener("resize", () => map.invalidateSize());

  const segLayer = L.layerGroup().addTo(map);
  const nodeLayer = L.layerGroup().addTo(map);

  // ---- controls ----
  const supplyEl = document.getElementById("dh-supply") as HTMLInputElement;
  const groundEl = document.getElementById("dh-ground") as HTMLInputElement;
  const typeEl = document.getElementById("dh-type") as HTMLSelectElement;
  const maxPaEl = document.getElementById("dh-maxpa") as HTMLInputElement;
  const btnBoiler = document.getElementById("dh-place-boiler") as HTMLButtonElement;
  const btnBranch = document.getElementById("dh-branch") as HTMLButtonElement;
  const btnDelete = document.getElementById("dh-delete") as HTMLButtonElement;
  const btnClear = document.getElementById("dh-clear") as HTMLButtonElement;
  const btnOptimize = document.getElementById("dh-optimize") as HTMLButtonElement | null;
  const hintEl = document.getElementById("dh-hint");

  [supplyEl, groundEl, typeEl, maxPaEl].forEach((el) => el?.addEventListener("input", () => renderAll()));

  const supply = () => clampNum(Number(supplyEl?.value), 30, 130, 80);
  const ground = () => clampNum(Number(groundEl?.value), -10, 30, 10);
  const maxPa = () => clampNum(Number(maxPaEl?.value), 50, 600, 150);
  const pipeType = (): PipeType => (typeEl?.value === "Duo" || typeEl?.value === "Single" ? (typeEl.value as PipeType) : "Auto");

  function setHint(msg: string) { if (hintEl) hintEl.textContent = msg; }

  // ---- fullscreen ------------------------------------------------------------
  // Inline styles rather than Tailwind classes: these are applied at runtime and
  // the CSS build would purge classes it never sees in the source.
  const wrap = document.getElementById("dh-mapwrap");
  const fsBtn = document.getElementById("dh-fullscreen") as HTMLButtonElement | null;
  let fullscreen = false;
  function setFullscreen(on: boolean) {
    if (!wrap || !fsBtn) return;
    fullscreen = on;
    const st = wrap.style;
    if (on) {
      st.position = "fixed";
      st.inset = "0";
      st.zIndex = "2000";
      st.margin = "0";
      mapEl.style.height = "100vh";
      mapEl.style.borderRadius = "0";
      document.body.style.overflow = "hidden";
    } else {
      st.position = "";
      st.inset = "";
      st.zIndex = "";
      st.margin = "";
      mapEl.style.height = "";
      mapEl.style.borderRadius = "";
      document.body.style.overflow = "";
    }
    const icon = fsBtn.querySelector("i");
    if (icon) { icon.classList.toggle("fa-expand", !on); icon.classList.toggle("fa-compress", on); }
    setTimeout(() => map.invalidateSize(), 60);
  }
  fsBtn?.addEventListener("click", () => setFullscreen(!fullscreen));
  document.addEventListener("keydown", (e) => { if (e.key === "Escape" && fullscreen) setFullscreen(false); });

  // ---- elevation -------------------------------------------------------------
  // Open-Meteo's elevation API is free, keyless and worldwide (the planner's
  // swisstopo profile is Switzerland-only). Fetched per node, cached on the node.
  let elevTimer: number | null = null;
  function refreshElevation(): Promise<void> {
    if (elevTimer != null) clearTimeout(elevTimer);
    return new Promise((resolve) => {
      elevTimer = window.setTimeout(() => void fetchElevation().then(resolve), 250);
    });
  }
  async function fetchElevation(): Promise<void> {
    const pending = state.nodes.filter((n) => n.elevM === undefined).slice(0, 100);
    if (!pending.length) return;
    const lat = pending.map((n) => n.lat.toFixed(5)).join(",");
    const lng = pending.map((n) => n.lng.toFixed(5)).join(",");
    try {
      const r = await fetch(`https://api.open-meteo.com/v1/elevation?latitude=${lat}&longitude=${lng}`);
      const j = (await r.json()) as { elevation?: number[] };
      const arr = j.elevation ?? [];
      pending.forEach((n, i) => { n.elevM = Number.isFinite(arr[i]) ? arr[i]! : null; });
    } catch {
      // Offline / blocked: mark as unavailable so we stop retrying every render.
      pending.forEach((n) => { n.elevM = null; });
    }
    renderAll();
  }

  // ---- map click modes -------------------------------------------------------
  // The run always starts at the selected node, so a branch needs exactly one
  // click: where it ends. Bends come from tapping a run to drop a junction.
  type Mode = "idle" | "boiler" | "branch";
  let mode: Mode = "idle";

  function setMode(next: Mode) {
    mode = next;
    mapEl.style.cursor = next === "idle" ? "" : "crosshair";
    btnBranch.classList.toggle("btn-danger", next === "branch");
    setHint(next === "boiler" ? tr("hint_place_boiler") : next === "branch" ? tr("hint_branch") : "");
  }

  btnBoiler.addEventListener("click", () => setMode(mode === "boiler" ? "idle" : "boiler"));
  btnBranch.addEventListener("click", () => startBranch());

  function startBranch() {
    if (!selectedId || !nodeById(selectedId)) return;
    setMode(mode === "branch" ? "idle" : "branch");
  }

  map.on("click", (e: any) => {
    const ll = e.latlng as L.LatLng;
    if (mode === "boiler") {
      const boiler = boilerNode();
      if (boiler) { boiler.lat = ll.lat; boiler.lng = ll.lng; }
      else state.nodes.push({ id: genId(), lat: ll.lat, lng: ll.lng, kind: "boiler", powerKw: 0, deltaT: 0, name: tr("boiler") });
      setMode("idle");
      void refreshElevation();
      renderAll();
      return;
    }
    if (mode === "branch") {
      const src = selectedId ? nodeById(selectedId) : null;
      if (!src) { setMode("idle"); return; }
      const child: NodeRec = { id: genId(), lat: ll.lat, lng: ll.lng, kind: "node", powerKw: 10, deltaT: 20, name: estateName(), wallEntry: "mauerkragen" };
      state.nodes.push(child);
      state.segments.push({ id: genId(), fromId: src.id, toId: child.id, latlngs: [[src.lat, src.lng], [ll.lat, ll.lng]] });
      selectedId = child.id;
      // Keep branching from the same source is rarely wanted; select the new end.
      setMode("idle");
      void refreshElevation();
      renderAll();
      return;
    }
    // Clicking empty map clears the selection.
    if (selectedId) { selectedId = null; renderAll(); }
  });

  // ---- delete / clear ----
  btnDelete.addEventListener("click", () => {
    if (!selectedId) return;
    deleteSubtree(selectedId);
    selectedId = null;
    renderAll();
  });
  // ---- geometric optimisation ------------------------------------------------
  // The boiler and the estates are real places and stay put; the junctions are
  // free points. Minimising Σ(weight × length) over them is the weighted Fermat
  // (Steiner) problem, solved by Weiszfeld iteration. Weighting each run by its
  // pressure gradient (Pa/m) pulls junctions so the hardest-working trunks get
  // shortest — which cuts pipe, pump head and heat loss at once.
  btnOptimize?.addEventListener("click", () => optimize());

  function metrics(): { totalLen: number; pumpBar: number; minSupply: number } {
    const supplyAt = recompute() ?? new Map<string, number>();
    const { pumpBar, minSupply } = pathStats(supplyAt);
    const totalLen = state.segments.reduce((a, sg) => a + (results.get(sg.id)?.lengthM ?? 0), 0);
    return { totalLen, pumpBar, minSupply };
  }

  function optimize() {
    const junctions = state.nodes.filter((n) => n.kind !== "boiler" && isJunction(n.id));
    if (!junctions.length || !boilerNode()) { setHint(tr("optimize_none")); return; }

    const before = metrics();
    // Sizing depends on power and ΔT, not on geometry, so these weights hold
    // still while the junctions move — no need to re-size inside the loop.
    const weightOf = (segId: string) => Math.max(1, results.get(segId)?.dropPerM ?? 1);
    const project = (n: NodeRec) => map.project([n.lat, n.lng], 20);

    for (let iter = 0; iter < 200; iter++) {
      let moved = false;
      for (const j of junctions) {
        const links: { p: L.Point; w: number }[] = [];
        const parentSeg = state.segments.find((sg) => sg.toId === j.id);
        if (parentSeg) {
          const pn = nodeById(parentSeg.fromId);
          if (pn) links.push({ p: project(pn), w: weightOf(parentSeg.id) });
        }
        for (const ks of childrenOf(j.id)) {
          const cn = nodeById(ks.toId);
          if (cn) links.push({ p: project(cn), w: weightOf(ks.id) });
        }
        if (links.length < 2) continue;

        const cur = project(j);
        let sx = 0, sy = 0, sw = 0;
        let onTop = false;
        for (const l of links) {
          const d = Math.hypot(cur.x - l.p.x, cur.y - l.p.y);
          if (d < 1e-9) { onTop = true; break; } // sitting on a neighbour: optimal already
          const w = l.w / d;
          sx += l.p.x * w;
          sy += l.p.y * w;
          sw += w;
        }
        if (onTop || sw <= 0) continue;
        const nx = sx / sw;
        const ny = sy / sw;
        if (Math.hypot(nx - cur.x, ny - cur.y) > 0.02) moved = true;
        const ll = map.unproject(L.point(nx, ny), 20);
        j.lat = ll.lat;
        j.lng = ll.lng;
      }
      if (!moved) break;
    }

    // Pull the runs onto their relocated junctions.
    for (const j of junctions) {
      for (const sg of state.segments) {
        if (sg.fromId === j.id) sg.latlngs[0] = [j.lat, j.lng];
        if (sg.toId === j.id) sg.latlngs[sg.latlngs.length - 1] = [j.lat, j.lng];
      }
    }

    // Measure on the same elevations we started with, then refresh the terrain
    // under the moved junctions (which re-renders with the true heights).
    const after = metrics();
    for (const j of junctions) j.elevM = undefined;
    renderAll();
    void refreshElevation();

    const dLen = before.totalLen - after.totalLen;
    const dBar = before.pumpBar - after.pumpBar;
    const dSup = after.minSupply - before.minSupply;
    setHint(
      dLen <= 0.5 && Math.abs(dBar) < 0.005
        ? tr("optimize_already")
        : tr("optimize_done", { m: nf0.format(Math.max(0, dLen)), bar: nf1.format(dBar), k: nf2.format(dSup) }),
    );
  }

  btnClear.addEventListener("click", () => {
    if (!state.nodes.length || !confirm(tr("confirm_clear"))) return;
    state.nodes = [];
    state.segments = [];
    selectedId = null;
    renderAll();
  });

  // ---------------------------------------------------------------------------
  // Tree helpers
  // ---------------------------------------------------------------------------
  function nodeById(id: string): NodeRec | undefined { return state.nodes.find((n) => n.id === id); }
  function childrenOf(id: string): SegRec[] { return state.segments.filter((sg) => sg.fromId === id); }
  function boilerNode(): NodeRec | undefined { return state.nodes.find((n) => n.kind === "boiler"); }

  function deleteSubtree(id: string) {
    const node = nodeById(id);
    if (!node) return;
    if (node.kind === "boiler") {
      // Deleting the source drops the whole network.
      state.nodes = [];
      state.segments = [];
      return;
    }
    const toRemove = new Set<string>();
    const visit = (n: string) => {
      toRemove.add(n);
      for (const sg of childrenOf(n)) visit(sg.toId);
    };
    visit(id);
    state.nodes = state.nodes.filter((n) => !toRemove.has(n.id));
    state.segments = state.segments.filter((sg) => !toRemove.has(sg.toId) && !toRemove.has(sg.fromId));
  }

  /**
   * Loads (kW/ΔT) in the subtree rooted at `id`. Only estates — leaf nodes — carry
   * demand: a junction's power is the recursive sum of its branches, so counting
   * anything it holds itself would double-count it.
   */
  function subtreeLoads(id: string, seen = new Set<string>()): { kW: number; deltaT: number }[] {
    if (seen.has(id)) return [];
    seen.add(id);
    const node = nodeById(id);
    const kids = childrenOf(id);
    if (node && node.kind !== "boiler" && kids.length === 0) {
      return node.powerKw > 0 && node.deltaT > 0 ? [{ kW: node.powerKw, deltaT: node.deltaT }] : [];
    }
    const out: { kW: number; deltaT: number }[] = [];
    for (const sg of kids) out.push(...subtreeLoads(sg.toId, seen));
    return out;
  }

  /** Read-only rollup shown on a junction: what its branches add up to. */
  function junctionRollup(id: string): { kW: number; deltaT: number; estates: number } {
    const loads = subtreeLoads(id);
    return { kW: loads.reduce((a, l) => a + l.kW, 0), deltaT: effectiveDeltaT(loads), estates: loads.length };
  }

  // ---------------------------------------------------------------------------
  // Parts: wall entries at estates, T-pieces at junctions
  // ---------------------------------------------------------------------------

  /** A node with outgoing runs is a junction (a pure tee); a leaf is an estate. */
  function isJunction(nodeId: string): boolean {
    return childrenOf(nodeId).length > 0;
  }
  function isEstate(n: NodeRec): boolean {
    return n.kind !== "boiler" && !isJunction(n.id);
  }

  /**
   * The jacket Ø (mm) a node's wall entry has to seal. For an estate that's the
   * pipe feeding it; for the boiler it's the largest pipe leaving it.
   */
  function jacketMmFor(nodeId: string): number | null {
    const jacketOf = (segId: string): number | null => {
      const m = (results.get(segId)?.product?.jacket ?? "").match(/(\d+(?:\.\d+)?)/);
      return m ? Number(m[1]) : null;
    };
    const n = nodeById(nodeId);
    if (n?.kind === "boiler") {
      const out = childrenOf(nodeId).map((sg) => jacketOf(sg.id)).filter((x): x is number => x != null);
      return out.length ? Math.max(...out) : null;
    }
    const seg = state.segments.find((sg) => sg.toId === nodeId);
    return seg ? jacketOf(seg.id) : null;
  }

  /** Anything that penetrates a wall: every estate, plus the boiler house. */
  function wallNodes(): NodeRec[] {
    return state.nodes.filter((n) => (n.kind === "boiler" ? childrenOf(n.id).length > 0 : isEstate(n) && n.powerKw > 0));
  }

  function defaultWallKind(n: NodeRec): WallEntryKind {
    return n.wallEntry ?? (n.kind === "boiler" ? "pressring" : "mauerkragen");
  }

  type WallPick = { sku: string; name: string; priceText: string; priceCents: number | null; currency: string; href: string; variantId: number; note: string };

  /**
   * The wall part for a node. Mauerkragen/Pressring are matched on the Ø range
   * they seal; a Dichtungskette instead needs a cored bore, so the requirement is
   * snapped to the nearest 50 mm and we take the smallest chain that covers it
   * (preferring EPDM, the general-purpose material).
   */
  function pickWall(kind: WallEntryKind, jacketMm: number | null): WallPick | null {
    if (jacketMm == null) return null;
    if (kind === "dichtungskette") {
      const bore = boreThresholdMm(jacketMm);
      const fits = data.chains.filter((c) => c.size >= bore);
      if (!fits.length) return null;
      const smallest = Math.min(...fits.map((c) => c.size));
      const atSize = fits.filter((c) => c.size === smallest);
      const chain = atSize.find((c) => c.material === "epdm") ?? atSize[0]!;
      return {
        variantId: chain.variantId, sku: chain.sku, name: chain.name,
        priceText: chain.priceText, priceCents: chain.priceCents ?? null, currency: chain.currency ?? data.currency,
        href: chain.href, note: tr("bore_note", { bore: String(bore) }),
      };
    }
    const fit = data.wallEntries.find((o) => o.kind === kind && jacketMm >= o.minMm && jacketMm <= o.maxMm);
    if (!fit) return null;
    return {
      variantId: fit.variantId, sku: fit.sku, name: fit.name, priceText: fit.priceText,
      priceCents: fit.priceCents, currency: fit.currency, href: fit.href,
      note: `${fit.minMm}–${fit.maxMm} mm`,
    };
  }

  /**
   * T-pieces implied at a node: every outgoing run beyond the first needs a tee.
   * (Junctions are pure tees — they carry no demand of their own.)
   */
  function teeCount(nodeId: string): number {
    return Math.max(0, childrenOf(nodeId).length - 1);
  }
  function totalTees(): number {
    return state.nodes.reduce((a, n) => a + teeCount(n.id), 0);
  }

  /** Elevation extremes over the network (null until the terrain lookup lands). */
  function elevStats(): { min: number; max: number; spread: number; staticBar: number } | null {
    const els = state.nodes.map((n) => n.elevM).filter((x): x is number => typeof x === "number");
    if (els.length < 2) return null;
    const min = Math.min(...els);
    const max = Math.max(...els);
    const spread = max - min;
    return { min, max, spread, staticBar: spread * BAR_PER_M_WATER };
  }

  /** Weakest pressure rating (PN) among the pipes actually used. */
  function minPn(): number | null {
    let pn: number | null = null;
    for (const sg of state.segments) {
      const name = results.get(sg.id)?.product?.name ?? "";
      const m = name.match(/PN\s*(\d+)/i);
      if (!m) continue;
      const v = Number(m[1]);
      if (pn == null || v < pn) pn = v;
    }
    return pn;
  }

  // ---------------------------------------------------------------------------
  // Engineering computation
  // ---------------------------------------------------------------------------
  /** Planar (map footprint) length. */
  function planarLengthM(sg: SegRec): number {
    let total = 0;
    for (let i = 1; i < sg.latlngs.length; i++) total += map.distance(sg.latlngs[i - 1]!, sg.latlngs[i]!);
    return total;
  }

  /**
   * Trench length, slope-corrected when both ends have an elevation:
   * √(planar² + Δh²). A 200 m run climbing 40 m is really ~204 m of pipe.
   */
  function segLengthM(sg: SegRec): number {
    const planar = planarLengthM(sg);
    const dh = segRise(sg);
    return dh == null ? planar : Math.sqrt(planar * planar + dh * dh);
  }

  /** Elevation gain (m) from a segment's start to its end; null if unknown. */
  function segRise(sg: SegRec): number | null {
    const a = nodeById(sg.fromId)?.elevM;
    const b = nodeById(sg.toId)?.elevM;
    if (a == null || b == null) return null;
    return b - a;
  }

  function recompute() {
    results = new Map();
    const boiler = boilerNode();
    if (!boiler) return;
    const mp = maxPa();
    const pt = pipeType();

    // Supply temperature at each node (BFS from boiler), driving heat loss.
    const supplyAt = new Map<string, number>();
    supplyAt.set(boiler.id, supply());

    const queue: string[] = [boiler.id];
    while (queue.length) {
      const from = queue.shift()!;
      const fromTemp = supplyAt.get(from) ?? supply();
      for (const sg of childrenOf(from)) {
        const loads = subtreeLoads(sg.toId);
        const kW = loads.reduce((a, l) => a + l.kW, 0);
        const effDt = effectiveDeltaT(loads);
        const mass = loads.reduce((a, l) => a + massFlowKgS(l.kW, l.deltaT), 0);
        const flow = loads.reduce((a, l) => a + flowM3h(l.kW, l.deltaT), 0);
        const lengthM = segLengthM(sg);

        let res: SegResult;
        if (kW <= 0 || effDt <= 0) {
          res = { lengthM, powerKw: 0, effDeltaT: 0, flow: 0, diameter: 0, type: pt === "Single" ? "Single" : "Duo", dropPerM: 0, dropPa: 0, tempDropK: 0, product: null, exceedsGradient: false, noProduct: false };
        } else {
          const chosen = chooseSegmentPipe({ powerKw: kW, deltaT: effDt, maxPaPerM: mp, pipeType: pt, products: data.products });
          const diameter = chosen?.diameter ?? 0;
          const type = chosen?.type ?? (pt === "Single" ? "Single" : "Duo");
          const product = chosen ? data.products.find((p) => Math.round(p.diameter) === diameter && p.type === type) ?? null : null;
          const dropPerM = chosen?.dropPerM ?? 0;
          const u = product ? uWertFor(type, product.jacket, diameter) : uWertFor(type, "", diameter);
          const dT = u != null ? tempDropK(u, lengthM, fromTemp, ground(), mass) : 0;
          res = {
            lengthM, powerKw: kW, effDeltaT: effDt, flow, diameter, type, dropPerM,
            dropPa: dropPerM * lengthM * 2, // supply + return
            tempDropK: dT, product, exceedsGradient: chosen?.exceedsGradient ?? false, noProduct: !product,
          };
        }
        results.set(sg.id, res);
        supplyAt.set(sg.toId, fromTemp - res.tempDropK);
        queue.push(sg.toId);
      }
    }
    return supplyAt;
  }

  // Worst-case pump head (bar) and min arriving supply temp, from path sums.
  function pathStats(supplyAt: Map<string, number>) {
    const boiler = boilerNode();
    if (!boiler) return { pumpBar: 0, minSupply: supply() };
    const dropTo = new Map<string, number>();
    dropTo.set(boiler.id, 0);
    let maxDrop = 0;
    let minSupply = supply();
    const queue = [boiler.id];
    while (queue.length) {
      const from = queue.shift()!;
      for (const sg of childrenOf(from)) {
        const r = results.get(sg.id);
        const d = (dropTo.get(from) ?? 0) + (r?.dropPa ?? 0);
        dropTo.set(sg.toId, d);
        if (d > maxDrop) maxDrop = d;
        const node = nodeById(sg.toId);
        if (node && childrenOf(node.id).length === 0) {
          const t = supplyAt.get(node.id) ?? supply();
          if (t < minSupply) minSupply = t;
        }
        queue.push(sg.toId);
      }
    }
    return { pumpBar: maxDrop / 1e5, minSupply };
  }

  // ---------------------------------------------------------------------------
  // Map rendering
  // ---------------------------------------------------------------------------
  function segColor(r: SegResult | undefined): string {
    if (!r || !r.powerKw) return "#94a3b8";
    if (r.noProduct) return "#dc2626";
    if (r.exceedsGradient) return "#d97706";
    return "#2563eb";
  }

  function renderMap() {
    segLayer.clearLayers();
    nodeLayer.clearLayers();

    for (const sg of state.segments) {
      const r = results.get(sg.id);
      const line = L.polyline(sg.latlngs, { color: segColor(r), weight: 5, opacity: 0.9 });
      if (r && r.powerKw > 0) {
        const label = r.diameter ? `${nf0.format(r.powerKw)} kW · Ø${r.diameter} ${r.type} · ${fmtM(r.lengthM)}` : `${nf0.format(r.powerKw)} kW · ${fmtM(r.lengthM)}`;
        line.bindTooltip(label, { sticky: true, direction: "top", opacity: 0.9 });
      }
      line.on("click", (ev: any) => { (L as any).DomEvent.stop(ev); insertJunction(sg, ev.latlng); });
      segLayer.addLayer(line);
    }

    for (const n of state.nodes) {
      const isLeaf = n.kind !== "boiler" && childrenOf(n.id).length === 0;
      const marker = L.marker([n.lat, n.lng], { icon: nodeIcon(n, isLeaf, n.id === selectedId), draggable: true });
      marker.on("click", () => { selectedId = n.id; renderAll(); });
      marker.on("dragend", (ev: any) => {
        const ll = ev.target.getLatLng();
        n.lat = ll.lat; n.lng = ll.lng;
        n.elevM = undefined; // moved → the ground under it changed
        // Move connected segment endpoints to follow the node.
        for (const sg of state.segments) {
          if (sg.fromId === n.id) sg.latlngs[0] = [ll.lat, ll.lng];
          if (sg.toId === n.id) sg.latlngs[sg.latlngs.length - 1] = [ll.lat, ll.lng];
        }
        void refreshElevation();
        renderAll();
      });
      nodeLayer.addLayer(marker);
    }
  }

  function nodeIcon(n: NodeRec, isLeaf: boolean, selected: boolean): L.DivIcon {
    const color = n.kind === "boiler" ? "#dc2626" : isLeaf && n.powerKw > 0 ? "#16a34a" : "#475569";
    const icon = n.kind === "boiler" ? "fire" : isLeaf ? "house" : "circle-nodes";
    const ring = selected ? "box-shadow:0 0 0 3px #2563eb;" : "";
    const html = `<div style="width:26px;height:26px;border-radius:50%;background:${color};${ring}display:flex;align-items:center;justify-content:center;color:#fff;border:2px solid #fff;font-size:12px"><i class="fa-solid fa-${icon}"></i></div>`;
    return L.divIcon({ html, className: "", iconSize: [26, 26], iconAnchor: [13, 13] });
  }

  // Insert a junction where a segment was clicked (split it), enabling mid-line
  // branching from any point on a line.
  function insertJunction(sg: SegRec, latlng: L.LatLng) {
    const click = map.latLngToLayerPoint(latlng);
    // Find the best insertion index by projecting onto each sub-segment.
    let bestIdx = 0;
    let bestPt: [number, number] = sg.latlngs[0]!;
    let bestDist = Infinity;
    for (let i = 1; i < sg.latlngs.length; i++) {
      const a = map.latLngToLayerPoint(L.latLng(sg.latlngs[i - 1]!));
      const b = map.latLngToLayerPoint(L.latLng(sg.latlngs[i]!));
      const proj = projectPoint(click, a, b);
      const d = click.distanceTo(proj);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
        const ll = map.layerPointToLatLng(proj);
        bestPt = [ll.lat, ll.lng];
      }
    }
    const junction: NodeRec = { id: genId(), lat: bestPt[0], lng: bestPt[1], kind: "node", powerKw: 0, deltaT: 20, name: tr("junction") };
    const head = sg.latlngs.slice(0, bestIdx).concat([bestPt]);
    const tail = ([bestPt] as [number, number][]).concat(sg.latlngs.slice(bestIdx));
    // Replace the original segment with two, meeting at the junction.
    state.nodes.push(junction);
    state.segments = state.segments.filter((x) => x.id !== sg.id);
    state.segments.push({ id: genId(), fromId: sg.fromId, toId: junction.id, latlngs: head });
    state.segments.push({ id: genId(), fromId: junction.id, toId: sg.toId, latlngs: tail });
    selectedId = junction.id;
    renderAll();
  }

  // ---------------------------------------------------------------------------
  // Selected-node editor
  // ---------------------------------------------------------------------------
  function renderSelected() {
    const n = selectedId ? nodeById(selectedId) : null;
    btnBranch.disabled = !n;
    btnDelete.disabled = !n;
    if (!n) { selectedEl.classList.add("hidden"); selectedEl.innerHTML = ""; return; }
    selectedEl.classList.remove("hidden");
    const isBoiler = n.kind === "boiler";
    const junction = !isBoiler && isJunction(n.id);
    const jacket = jacketMmFor(n.id);
    const needsWall = isBoiler ? childrenOf(n.id).length > 0 : isEstate(n) && n.powerKw > 0;
    const kind = defaultWallKind(n);
    const pick = needsWall ? pickWall(kind, jacket) : null;
    const fitLine = needsWall
      ? `<div class="mt-1 text-[11px] ${pick ? "text-slate-500" : "text-amber-700"}">${
          pick
            ? `${esc(pick.sku)} · ${esc(pick.priceText)} <span class="text-slate-400">(${esc(pick.note)})</span>`
            : esc(tr("wall_none"))
        }</div>`
      : "";
    const wall = needsWall
      ? `<label class="mt-2 block"><span class="label">${esc(tr("wall_entry"))}</span>
           <select id="dh-wall" class="select h-8 text-sm">
             <option value="mauerkragen"${kind === "mauerkragen" ? " selected" : ""}>${esc(tr("wall_mauerkragen"))}</option>
             <option value="pressring"${kind === "pressring" ? " selected" : ""}>${esc(tr("wall_pressring"))}</option>
             <option value="dichtungskette"${kind === "dichtungskette" ? " selected" : ""}>${esc(tr("wall_dichtungskette"))}</option>
           </select>${fitLine}</label>`
      : "";
    const elev = `<div class="mt-2 text-[11px] text-slate-500">${esc(tr("elevation"))}: ${
      n.elevM == null ? "—" : `${nf0.format(n.elevM)} m`
    }</div>`;

    let demand: string;
    if (isBoiler) {
      demand = `<p class="text-xs text-slate-500">${esc(tr("boiler_note"))}</p>${wall}`;
    } else if (junction) {
      // A junction carries no demand of its own — it's the sum of its branches.
      const roll = junctionRollup(n.id);
      demand = `<div class="rounded-md bg-slate-50 px-2.5 py-2">
          <div class="text-[10px] uppercase tracking-wide text-slate-400">${esc(tr("branch_power"))}</div>
          <div class="text-lg font-semibold tabular-nums text-slate-900">${nf1.format(roll.kW)} <span class="text-xs font-normal text-slate-400">kW</span></div>
          <div class="mt-0.5 text-[11px] text-slate-500">${esc(tr("delta_t"))} ${nf0.format(roll.deltaT)} K · ${roll.estates} ${esc(tr("estates"))}</div>
        </div>
        <p class="mt-1 text-[11px] text-slate-400">${esc(tr("junction_readonly"))}</p>`;
    } else {
      demand = `<div class="grid grid-cols-2 gap-2">
           <label class="block"><span class="label">${esc(tr("power_kw"))}</span>
             <input id="dh-kw" type="number" min="0" step="1" value="${n.powerKw}" class="input h-8 text-sm" /></label>
           <label class="block"><span class="label">${esc(tr("delta_t"))}</span>
             <input id="dh-dt" type="number" min="1" max="60" step="1" value="${n.deltaT}" class="input h-8 text-sm" /></label>
         </div>${wall}`;
    }
    selectedEl.innerHTML = `
      <div class="flex items-center justify-between">
        <span class="text-xs font-semibold uppercase tracking-wide text-slate-500">${esc(isBoiler ? tr("boiler") : junction ? tr("junction") : tr("estate"))}</span>
        <button id="dh-deselect" class="text-slate-400 hover:text-slate-700" title="${esc(tr("close"))}"><i class="fa-solid fa-xmark"></i></button>
      </div>
      <label class="mt-2 block"><span class="label">${esc(tr("name"))}</span>
        <input id="dh-name" value="${esc(n.name)}" class="input h-8 text-sm" /></label>
      <div class="mt-2">${demand}</div>
      ${elev}
      <div class="mt-3 flex gap-2">
        <button id="dh-branch2" class="btn-primary h-8 flex-1 text-xs"><i class="fa-solid fa-code-branch"></i> ${esc(tr("branch"))}</button>
        <button id="dh-del2" class="btn h-8 text-xs text-red-700"><i class="fa-solid fa-trash"></i></button>
      </div>`;
    selectedEl.querySelector("#dh-deselect")?.addEventListener("click", () => { selectedId = null; renderAll(); });
    selectedEl.querySelector("#dh-branch2")?.addEventListener("click", () => startBranch());
    selectedEl.querySelector("#dh-del2")?.addEventListener("click", () => { deleteSubtree(n.id); selectedId = null; renderAll(); });
    selectedEl.querySelector("#dh-name")?.addEventListener("input", (e) => { n.name = (e.target as HTMLInputElement).value; saveSoon(); });
    selectedEl.querySelector("#dh-kw")?.addEventListener("input", (e) => { n.powerKw = Math.max(0, Number((e.target as HTMLInputElement).value) || 0); renderAll(); });
    selectedEl.querySelector("#dh-dt")?.addEventListener("input", (e) => { n.deltaT = clampNum(Number((e.target as HTMLInputElement).value), 1, 60, 20); renderAll(); });
    selectedEl.querySelector("#dh-wall")?.addEventListener("change", (e) => {
      const v = (e.target as HTMLSelectElement).value;
      n.wallEntry = v === "pressring" ? "pressring" : v === "dichtungskette" ? "dichtungskette" : "mauerkragen";
      renderAll();
    });
  }

  // ---------------------------------------------------------------------------
  // Results panel (totals, feasibility, segment table, Mermaid, cart)
  // ---------------------------------------------------------------------------
  function renderResults(supplyAt: Map<string, number>) {
    const boiler = boilerNode();
    if (!boiler) {
      resultsEl.innerHTML = `<div class="card p-6 text-center text-sm text-slate-500">${esc(tr("empty_boiler"))}</div>`;
      return;
    }
    const segs = state.segments.filter((sg) => (results.get(sg.id)?.powerKw ?? 0) > 0);
    if (!segs.length) {
      resultsEl.innerHTML = `<div class="card p-6 text-center text-sm text-slate-500">${esc(tr("empty_branch"))}</div>`;
      // still show mermaid of the bare tree if any segments exist
      if (state.segments.length) renderMermaid();
      return;
    }

    // Only estates (leaves) carry demand — a junction's power is derived.
    const demandNodes = state.nodes.filter((n) => isEstate(n) && n.powerKw > 0 && n.deltaT > 0);
    const systemKw = demandNodes.reduce((a, n) => a + n.powerKw, 0);
    const boilerFlow = demandNodes.reduce((a, n) => a + flowM3h(n.powerKw, n.deltaT), 0);
    const totalLen = state.segments.reduce((a, sg) => a + (results.get(sg.id)?.lengthM ?? 0), 0);
    const { pumpBar, minSupply } = pathStats(supplyAt);
    const estates = demandNodes.length;

    // Feasibility warnings
    const warns: string[] = [];
    for (const sg of segs) {
      const r = results.get(sg.id)!;
      const to = nodeById(sg.toId);
      if (r.noProduct) warns.push(tr("warn_no_product", { name: to?.name ?? "", d: r.diameter }));
      else if (r.exceedsGradient) warns.push(tr("warn_gradient", { name: to?.name ?? "", pa: nf0.format(r.dropPerM) }));
    }
    if (pumpBar > PUMP_WARN_BAR) warns.push(tr("warn_pump", { bar: nf1.format(pumpBar) }));

    // Elevation: static head adds to the pump head against the pipe's PN rating.
    const el = elevStats();
    const pn = minPn();
    const staticBar = el?.staticBar ?? 0;
    if (pn != null && staticBar + pumpBar > pn) {
      warns.push(tr("warn_pressure", { total: nf1.format(staticBar + pumpBar), pn: String(pn) }));
    }
    for (const w of wallNodes()) {
      if (!pickWall(defaultWallKind(w), jacketMmFor(w.id))) warns.push(`${w.name}: ${tr("wall_none")}`);
    }

    const tiles = [
      tile("bolt", tr("total_power"), `${nf1.format(systemKw)} <span class="text-sm font-normal text-slate-400">kW</span>`, "bg-brand-100 text-brand-700"),
      tile("gauge", tr("boiler_flow"), `${nf1.format(boilerFlow)} <span class="text-sm font-normal text-slate-400">m³/h</span>`, "bg-sky-100 text-sky-700"),
      tile("ruler", tr("total_length"), `${nf0.format(totalLen)} <span class="text-sm font-normal text-slate-400">m</span>`, "bg-slate-100 text-slate-700"),
      tile("arrow-down-wide-short", tr("pump_head"), `${nf1.format(pumpBar)} <span class="text-sm font-normal text-slate-400">bar</span>`, pumpBar > PUMP_WARN_BAR ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"),
      tile("house", tr("estates"), String(estates), "bg-violet-100 text-violet-700"),
      tile("temperature-half", tr("min_supply"), `${nf1.format(minSupply)} <span class="text-sm font-normal text-slate-400">°C</span>`, "bg-amber-100 text-amber-700"),
      tile(
        "mountain",
        tr("elev_spread"),
        el ? `${nf0.format(el.spread)} <span class="text-sm font-normal text-slate-400">m</span>` : "—",
        "bg-lime-100 text-lime-700",
      ),
      tile(
        "compress",
        tr("static_head"),
        el ? `${nf1.format(el.staticBar)} <span class="text-sm font-normal text-slate-400">bar</span>` : "—",
        pn != null && staticBar + pumpBar > pn ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700",
      ),
    ].join("");

    const feasibility = warns.length
      ? `<div class="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"><b>${esc(tr("check_warnings"))}</b><ul class="mt-1 list-disc pl-5">${warns.map((w) => `<li>${esc(w)}</li>`).join("")}</ul></div>`
      : `<div class="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"><i class="fa-solid fa-circle-check mr-1"></i>${esc(tr("check_ok"))}</div>`;

    resultsEl.innerHTML = `
      <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">${tiles}</div>
      <div class="mt-4">${feasibility}</div>
      <h2 class="mt-6 text-base font-semibold text-slate-700">${esc(tr("segments"))}</h2>
      <div class="card mt-2 overflow-x-auto">${segTable(segs)}</div>
      <h2 class="mt-6 text-base font-semibold text-slate-700">${esc(tr("parts"))}</h2>
      <div class="card mt-2 overflow-x-auto">${partsTable()}</div>
      <h2 class="mt-6 text-base font-semibold text-slate-700">${esc(tr("schematic"))}</h2>
      <div class="card mt-2 overflow-x-auto p-4"><div id="dh-mermaid" class="flex justify-center"></div></div>
      ${cartBlock(segs)}`;

    resultsEl.querySelector("#dh-add-all")?.addEventListener("click", (e) => { void addAllToCart(e.currentTarget as HTMLButtonElement); });
    renderMermaid();
  }

  function segTable(segs: SegRec[]): string {
    const head = `<tr>
      <th>${esc(tr("col_route"))}</th><th class="text-right">${esc(tr("col_length"))}</th>
      <th class="text-right">${esc(tr("col_power"))}</th><th class="text-right">ΔT</th>
      <th class="text-right">${esc(tr("col_flow"))}</th><th>${esc(tr("col_pipe"))}</th>
      <th class="text-right">Pa/m</th><th class="text-right">${esc(tr("col_drop"))}</th>
      <th class="text-right">${esc(tr("col_temp_drop"))}</th><th>${esc(tr("col_product"))}</th></tr>`;
    const rows = segs.map((sg) => {
      const r = results.get(sg.id)!;
      const from = nodeById(sg.fromId);
      const to = nodeById(sg.toId);
      const pipe = r.diameter ? `Ø${r.diameter} ${esc(r.type)}` : "—";
      const prod = r.product
        ? `<a href="${esc(r.product.href)}" class="text-brand-600" target="_blank">${esc(r.product.sku)}</a>`
        : `<span class="text-red-600">${esc(tr("no_match"))}</span>`;
      const gradientCls = r.noProduct ? "text-red-600" : r.exceedsGradient ? "text-amber-600" : "text-slate-700";
      return `<tr>
        <td class="text-xs">${esc(from?.name ?? "")} → <b>${esc(to?.name ?? "")}</b></td>
        <td class="text-right tabular-nums">${fmtM(r.lengthM)}</td>
        <td class="text-right tabular-nums">${nf1.format(r.powerKw)} kW</td>
        <td class="text-right tabular-nums">${nf0.format(r.effDeltaT)} K</td>
        <td class="text-right tabular-nums">${nf2.format(r.flow)}</td>
        <td>${pipe}</td>
        <td class="text-right tabular-nums ${gradientCls}">${nf0.format(r.dropPerM)}</td>
        <td class="text-right tabular-nums">${nf1.format(r.dropPa / 1000)} kPa</td>
        <td class="text-right tabular-nums">${nf2.format(r.tempDropK)} K</td>
        <td class="text-xs">${prod}</td>
      </tr>`;
    }).join("");
    return `<table class="table text-sm"><thead>${head}</thead><tbody>${rows}</tbody></table>`;
  }

  type PartLine = {
    variantId: number; name: string; sku: string; qty: number;
    unit: "m" | "pc"; priceCents: number; currency: string; href: string;
  };

  /** Everything the network needs: pipe by the metre, wall entries and tees by the piece. */
  function billOfParts(): PartLine[] {
    const lines = new Map<number, PartLine>();
    const add = (l: PartLine) => {
      const cur = lines.get(l.variantId);
      if (cur) cur.qty += l.qty;
      else lines.set(l.variantId, { ...l });
    };
    for (const sg of state.segments) {
      const r = results.get(sg.id);
      if (!r?.product || r.product.priceCents == null || r.powerKw <= 0) continue;
      add({ variantId: r.product.variantId, name: r.product.name, sku: r.product.sku, qty: r.lengthM, unit: "m", priceCents: r.product.priceCents, currency: r.product.currency, href: r.product.href });
    }
    for (const w of wallNodes()) {
      const pick = pickWall(defaultWallKind(w), jacketMmFor(w.id));
      if (!pick || pick.priceCents == null) continue;
      add({ variantId: pick.variantId, name: pick.name, sku: pick.sku, qty: 1, unit: "pc", priceCents: pick.priceCents, currency: pick.currency, href: pick.href });
    }
    const tees = totalTees();
    if (tees > 0 && data.tee && data.tee.priceCents != null) {
      add({ variantId: data.tee.variantId, name: data.tee.name, sku: data.tee.sku, qty: tees, unit: "pc", priceCents: data.tee.priceCents, currency: data.tee.currency, href: data.tee.href });
    }
    return Array.from(lines.values());
  }

  /** Per-estate wall entry + the implied T-pieces. */
  function partsTable(): string {
    const rows = wallNodes().map((w) => {
      const jacket = jacketMmFor(w.id);
      const kind = defaultWallKind(w);
      const pick = pickWall(kind, jacket);
      const label = kind === "pressring" ? tr("wall_pressring") : kind === "dichtungskette" ? tr("wall_dichtungskette") : tr("wall_mauerkragen");
      const part = pick
        ? `<a href="${esc(pick.href)}" target="_blank" class="text-brand-600">${esc(pick.sku)}</a> <span class="text-xs text-slate-400">${esc(pick.name)}</span>`
        : `<span class="text-amber-700">${esc(tr("wall_none"))}</span>`;
      return `<tr>
        <td class="text-xs">${esc(w.name)}${w.kind === "boiler" ? ` <span class="text-[10px] uppercase text-slate-400">${esc(tr("boiler"))}</span>` : ""}</td>
        <td>${esc(label)}${pick ? ` <span class="text-[10px] text-slate-400">${esc(pick.note)}</span>` : ""}</td>
        <td class="text-right tabular-nums">${jacket != null ? `Ø${jacket} mm` : "—"}</td>
        <td class="text-right tabular-nums">1</td>
        <td class="text-xs">${part}</td>
        <td class="text-right tabular-nums">${pick ? esc(pick.priceText) : "—"}</td>
      </tr>`;
    });
    const tees = totalTees();
    if (tees > 0) {
      const t = data.tee;
      rows.push(`<tr>
        <td class="text-xs">${esc(tr("tees"))}</td>
        <td>${esc(tr("col_tee"))}</td>
        <td class="text-right">—</td>
        <td class="text-right tabular-nums">${tees}</td>
        <td class="text-xs">${t ? `<a href="${esc(t.href)}" target="_blank" class="text-brand-600">${esc(t.sku)}</a> <span class="text-xs text-slate-400">${esc(t.name)}</span>` : `<span class="text-amber-700">${esc(tr("tee_none"))}</span>`}</td>
        <td class="text-right tabular-nums">${t ? esc(t.priceText) : "—"}</td>
      </tr>`);
    }
    if (!rows.length) return `<p class="p-4 text-sm text-slate-500">${esc(tr("empty_branch"))}</p>`;
    return `<table class="table text-sm"><thead><tr>
      <th>${esc(tr("col_route"))}</th><th>${esc(tr("col_wall"))}</th>
      <th class="text-right">Ø</th><th class="text-right">${esc(tr("col_qty"))}</th>
      <th>${esc(tr("col_part"))}</th><th class="text-right">${esc(tr("col_price"))}</th>
    </tr></thead><tbody>${rows.join("")}</tbody></table>`;
  }

  function cartBlock(_segs: SegRec[]): string {
    const bill = billOfParts();
    if (!bill.length) return "";
    const money = (cur: string) => new Intl.NumberFormat("de-DE", { style: "currency", currency: cur });
    let total = 0;
    const lines = bill.map((x) => {
      const q = x.unit === "m" ? Math.round(x.qty) : Math.round(x.qty);
      const cost = (x.priceCents / 100) * q;
      total += cost;
      return `<div class="flex items-center justify-between gap-2 border-b border-slate-100 py-1.5 text-sm last:border-0">
        <span class="min-w-0"><span class="font-medium text-slate-800">${esc(x.name)}</span> <span class="text-xs text-slate-400">${esc(x.sku)}</span></span>
        <span class="tabular-nums text-slate-600">${nf0.format(q)} ${x.unit === "m" ? "m" : "×"} · ${money(x.currency).format(cost)}</span>
      </div>`;
    }).join("");
    const cur = bill[0]!.currency;
    return `<div class="card mt-6 p-4">
      <div class="flex items-center justify-between">
        <h2 class="mt-0 text-base font-semibold text-slate-700">${esc(tr("order_pipes"))}</h2>
        <span class="text-sm font-semibold text-slate-900">${money(cur).format(total)}</span>
      </div>
      <div class="mt-2">${lines}</div>
      <button id="dh-add-all" class="btn-primary mt-3 gap-2"><i class="fa-solid fa-cart-plus"></i> ${esc(tr("add_all"))}</button>
    </div>`;
  }

  async function addAllToCart(btn: HTMLButtonElement) {
    const bill = billOfParts();
    if (!bill.length) return;
    btn.disabled = true;
    try {
      for (const line of bill) {
        const qty = Math.max(1, Math.round(line.qty));
        // Length goods carry metres in unitAmount; piece goods just use qty.
        const body: Record<string, string> = line.unit === "m"
          ? { variantId: String(line.variantId), qty: "1", unitAmount: String(qty) }
          : { variantId: String(line.variantId), qty: String(qty) };
        await fetch("/cart/add", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded", Accept: "application/json" },
          body: new URLSearchParams(body),
        });
      }
      bumpCount(bill.length);
      btn.innerHTML = `<i class="fa-solid fa-check"></i> ${esc(tr("added"))}`;
    } catch {
      /* ignore */
    } finally {
      setTimeout(() => { btn.disabled = false; btn.innerHTML = `<i class="fa-solid fa-cart-plus"></i> ${esc(tr("add_all"))}`; }, 1600);
    }
  }

  // ---------------------------------------------------------------------------
  // Mermaid schematic
  // ---------------------------------------------------------------------------
  function renderMermaid() {
    const el = document.getElementById("dh-mermaid");
    if (!el) return;
    const def = buildMermaid();
    loadMermaid()
      .then((mermaid: any) => mermaid.render("dhschema" + genId(), def))
      .then((out: any) => { el.innerHTML = out.svg; })
      .catch(() => { el.innerHTML = `<pre class="text-xs text-slate-500">${esc(def)}</pre>`; });
  }

  function buildMermaid(): string {
    const boiler = boilerNode();
    if (!boiler) return "graph TD\n  x[No boiler]";
    const idOf = (n: NodeRec) => "n" + n.id;
    const lines: string[] = ["graph TD"];
    // node declarations
    for (const n of state.nodes) {
      const isLeaf = n.kind !== "boiler" && childrenOf(n.id).length === 0;
      const label =
        n.kind === "boiler"
          ? `${mmLabel(n.name)}<br/>${nf1.format(junctionRollup(n.id).kW)} kW`
          : isLeaf
          ? n.powerKw > 0 ? `${mmLabel(n.name)}<br/>${nf1.format(n.powerKw)} kW` : mmLabel(n.name)
          // Junction: the recursive sum of everything downstream of it.
          : `${mmLabel(n.name)}<br/>${nf1.format(junctionRollup(n.id).kW)} kW`;
      if (n.kind === "boiler") lines.push(`  ${idOf(n)}[["${label}"]]:::boiler`);
      else if (isLeaf) lines.push(`  ${idOf(n)}("${label}"):::estate`);
      else lines.push(`  ${idOf(n)}(("${label}")):::junction`);
    }
    // edges
    for (const sg of state.segments) {
      const from = nodeById(sg.fromId);
      const to = nodeById(sg.toId);
      if (!from || !to) continue;
      const r = results.get(sg.id);
      const lbl = r && r.diameter ? `${nf0.format(r.powerKw)}kW Ø${r.diameter} ${fmtM(r.lengthM)}` : fmtM(r?.lengthM ?? segLengthM(sg));
      lines.push(`  ${idOf(from)} -->|"${mmLabel(lbl)}"| ${idOf(to)}`);
    }
    lines.push("classDef boiler fill:#fee2e2,stroke:#dc2626,color:#991b1b;");
    lines.push("classDef estate fill:#dcfce7,stroke:#16a34a,color:#166534;");
    lines.push("classDef junction fill:#e2e8f0,stroke:#475569,color:#334155;");
    return lines.join("\n");
  }

  // ---------------------------------------------------------------------------
  // Render orchestration + persistence
  // ---------------------------------------------------------------------------
  function renderAll() {
    const supplyAt = recompute() ?? new Map<string, number>();
    renderMap();
    renderSelected();
    renderResults(supplyAt);
    saveSoon();
  }

  let saveTimer: number | null = null;
  function saveSoon() {
    if (saveTimer != null) clearTimeout(saveTimer);
    saveTimer = window.setTimeout(save, 400);
  }
  function save() {
    const c = map.getCenter();
    state.view = { center: [c.lat, c.lng], zoom: map.getZoom() };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* quota */ }
  }
  function loadState(): Persisted {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const p = JSON.parse(raw) as Persisted;
        if (Array.isArray(p.nodes) && Array.isArray(p.segments)) return p;
      }
    } catch { /* ignore */ }
    return { nodes: [], segments: [] };
  }

  let estateCounter = state.nodes.filter((n) => n.kind !== "boiler").length;
  function estateName(): string { return tr("estate") + " " + ++estateCounter; }

  function bumpCount(n: number) {
    document.querySelectorAll("[data-cart-badge]").forEach((el) => {
      const c = parseInt(el.textContent || "0", 10) || 0;
      el.textContent = String(c + n);
    });
  }

  // Initial paint. Restore the map view to the saved network if present, and
  // look up elevation for any nodes restored from storage without one.
  if (state.view) map.setView(state.view.center, state.view.zoom);
  renderAll();
  void refreshElevation();
}

// ---------------------------------------------------------------------------
// Standalone helpers
// ---------------------------------------------------------------------------
function tile(icon: string, label: string, value: string, tone: string): string {
  return `<div class="card flex items-center gap-3 p-3">
    <span class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tone}"><i class="fa-solid fa-${icon}"></i></span>
    <div class="min-w-0"><div class="text-xl font-semibold leading-none text-slate-900">${value}</div>
    <div class="mt-1 truncate text-[10px] uppercase tracking-wide text-slate-500">${esc(label)}</div></div>
  </div>`;
}

function projectPoint(p: L.Point, a: L.Point, b: L.Point): L.Point {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return a;
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return L.point(a.x + t * dx, a.y + t * dy);
}

function fmtM(m: number): string {
  return m >= 1000 ? (m / 1000).toFixed(2) + " km" : Math.round(m) + " m";
}

function clampNum(n: number, lo: number, hi: number, dflt: number): number {
  if (!Number.isFinite(n)) return dflt;
  return Math.max(lo, Math.min(hi, n));
}

function mmLabel(s: string): string {
  // Mermaid-safe: strip quotes/brackets that would break node/edge syntax.
  return String(s).replace(/["\[\]{}()<>|]/g, "").slice(0, 40) || "?";
}

function toLatLngPairs(poly: L.Polyline): [number, number][] {
  const ll = poly.getLatLngs() as unknown[];
  const flat = (Array.isArray(ll[0]) ? (ll as L.LatLng[][]).flat() : (ll as L.LatLng[])) || [];
  return flat.map((p) => [p.lat, p.lng] as [number, number]);
}

function parseData(el: HTMLElement): DistrictData {
  const empty: DistrictData = { products: [], wallEntries: [], chains: [], tee: null, contact: { phone: "", email: "" }, currency: "EUR", lang: "en", strings: {} };
  try {
    return { ...empty, ...(JSON.parse(el.textContent || "{}") as Partial<DistrictData>) } as DistrictData;
  } catch {
    return empty;
  }
}

function genId(): string { return Math.random().toString(36).slice(2, 9); }

function esc(s: string | undefined): string {
  return String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

let mermaidPromise: Promise<any> | null = null;
function loadMermaid(): Promise<any> {
  if (mermaidPromise) return mermaidPromise;
  mermaidPromise = new Promise((resolve, reject) => {
    if ((window as any).mermaid) { (window as any).mermaid.initialize({ startOnLoad: false, theme: "neutral" }); return resolve((window as any).mermaid); }
    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js";
    script.onload = () => {
      const m = (window as any).mermaid;
      if (!m) return reject(new Error("mermaid missing"));
      m.initialize({ startOnLoad: false, theme: "neutral" });
      resolve(m);
    };
    script.onerror = () => reject(new Error("mermaid load failed"));
    document.head.appendChild(script);
  });
  return mermaidPromise;
}

let maplibrePromise: Promise<void> | null = null;
function loadMapLibre(): Promise<void> {
  if (maplibrePromise) return maplibrePromise;
  maplibrePromise = new Promise<void>((resolve, reject) => {
    if ((L as any).maplibreGL) return resolve();
    (window as any).L = L;
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = "https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.css";
    document.head.appendChild(css);
    const addScript = (src: string) =>
      new Promise<void>((res, rej) => {
        const sc = document.createElement("script");
        sc.src = src;
        sc.async = true;
        sc.onload = () => res();
        sc.onerror = () => rej(new Error("load failed: " + src));
        document.head.appendChild(sc);
      });
    addScript("https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.js")
      .then(() => addScript("https://unpkg.com/@maplibre/maplibre-gl-leaflet@0.0.22/leaflet-maplibre-gl.js"))
      .then(() => ((L as any).maplibreGL ? resolve() : reject(new Error("maplibreGL missing"))))
      .catch(reject);
  });
  return maplibrePromise;
}

function injectLeafletCss() {
  const links = [
    "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css",
    "https://unpkg.com/@geoman-io/leaflet-geoman-free@2.18.3/dist/leaflet-geoman.css",
  ];
  for (const href of links) {
    if (document.querySelector(`link[href="${href}"]`)) continue;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
  }
}
